// Opta event guide for average formation: include these typeIds regardless
// of outcome, plus 44/59/67 only when outcome === 0 (successful), and drop
// any event carrying qualifier 107 (Deleted event) or 6 (Attacking pass) as
// per the provided spec.
const AVERAGE_FORMATION_EVENT_TYPES = [
  1, 2, 3, 4, 7, 8, 10, 11, 12, 13, 14, 15, 16, 41, 42, 45, 49, 50, 51, 52, 53,
  54, 55, 56, 60, 61, 69, 72, 74,
];
const AVERAGE_FORMATION_OUTCOME_ZERO_ONLY_TYPES = [44, 59, 67];
const AVERAGE_FORMATION_EXCLUDED_QUALIFIER_IDS = [107, 6];

function isAverageFormationEvent(e) {
  if (!Number.isFinite(e?.x) || !Number.isFinite(e?.y)) return false;
  if (
    e?.qualifier?.some((q) =>
      AVERAGE_FORMATION_EXCLUDED_QUALIFIER_IDS.includes(q?.qualifierId),
    )
  )
    return false;
  if (AVERAGE_FORMATION_OUTCOME_ZERO_ONLY_TYPES.includes(e?.typeId)) {
    return e?.outcome === 0;
  }
  return AVERAGE_FORMATION_EVENT_TYPES.includes(e?.typeId);
}

export class AverageFormation {
  constructor({ animate, preview, update, animateOff }) {
    this.onAir = false;
    this.sendAnimate = animate;
    this.sendAnimateOff = animateOff;
    this.sendPreview = preview;
    this.sendUpdate = update;
    this.name = "AverageFormation";
    this.scene = "Average Formation";
    this.data = [];
  }

  // Map<opta_ID, {x: avgX, y: avgY}>, same 0-100 pitch scale TouchMap uses.
  buildAveragePositions({ events, team }) {
    const totals = new Map();

    events
      ?.filter((e) => e?.contestantId === team?.opta_ID)
      ?.filter(isAverageFormationEvent)
      ?.forEach((e) => {
        const entry = totals.get(e.playerId) || { sumX: 0, sumY: 0, count: 0 };
        entry.sumX += e.x;
        entry.sumY += e.y;
        entry.count += 1;
        totals.set(e.playerId, entry);
      });

    const averages = new Map();
    totals.forEach((entry, playerId) => {
      averages.set(playerId, {
        x: Math.round((entry.sumX / entry.count) * 100) / 100,
        y: Math.round((entry.sumY / entry.count) * 100) / 100,
      });
    });
    return averages;
  }

  preview({ options }) {
    this.onAir = false;
    this.data = [];

    const { team, game, opta_events, squads } = options;

    const averagePositions = this.buildAveragePositions({
      events: opta_events,
      team,
    });

    this.data.push({
      name: "Team Name",
      value: { text: team?.name || "" },
    });
    this.data.push({
      name: "Team Short Name",
      value: { text: team?.short_name || "" },
    });
    this.data.push({
      name: "Team Code",
      value: { text: team?.code || "" },
    });
    this.data.push({
      name: "Team Badge",
      value: { image: "${user}" + (team?.variables?.badge || "") },
    });
    this.data.push({
      name: "Team Secondary Badge",
      value: { image: "${user}" + (team?.variables?.alt_badge || "") },
    });

    this.data.push({
      name: "Competition",
      value: { text: game?.competition?.name || "" },
    });
    this.data.push({
      name: "Competition Colour",
      value: { colour: game?.competition?.colour || "" },
    });
    this.data.push({
      name: "Competition Badge",
      value: { image: "${user}" + (game?.competition?.badge || "") },
    });

    if (game?.home_team?._id === team?._id) {
      this.data.push({ name: "Team Type", value: { text: "home" } });
      this.data.push({
        name: "Team Kit BG Colour",
        value: {
          colour: team?.variables?.[game?.home_team_kit + "_kit_bg"] || "",
        },
      });
      this.data.push({
        name: "Team Kit Text Colour",
        value: {
          colour: team?.variables?.[game?.home_team_kit + "_kit_text"] || "",
        },
      });
    } else {
      this.data.push({ name: "Team Type", value: { text: "away" } });
      this.data.push({
        name: "Team Kit BG Colour",
        value: {
          colour: team?.variables?.[game?.away_team_kit + "_kit_bg"] || "",
        },
      });
      this.data.push({
        name: "Team Kit Text Colour",
        value: {
          colour: team?.variables?.[game?.away_team_kit + "_kit_text"] || "",
        },
      });
    }

    new Array(11).fill(1).forEach((_, index) => {
      let row = index + 1;

      let player = squads?.find((s) => s.order === row);
      let average = averagePositions.get(player?.squad?.player?.opta_ID);

      this.data.push({
        name: "First Name " + row,
        value: {
          text:
            player?.squad?.player?.first_name ??
            player?.squad?.player?.opta_first_name ??
            "",
        },
      });

      this.data.push({
        name: "Last Name " + row,
        value: {
          text:
            player?.squad?.player?.last_name ??
            player?.squad?.player?.opta_last_name ??
            "",
        },
      });

      this.data.push({
        name: "First Name Uppercase " + row,
        value: {
          text:
            player?.squad?.player?.first_name_upper ??
            player?.squad?.player?.opta_first_name?.toUpperCase() ??
            "",
        },
      });

      this.data.push({
        name: "Last Name Uppercase " + row,
        value: {
          text:
            player?.squad?.player?.last_name_upper ??
            player?.squad?.player?.opta_last_name?.toUpperCase() ??
            "",
        },
      });

      this.data.push({
        name: "Card " + row,
        value: {
          visible: player?.yellow_card || false,
        },
      });

      this.data.push({
        name: "Captain " + row,
        value: {
          visible: player?.captain || false,
        },
      });

      this.data.push({
        name: "Position " + row,
        value: {
          text: player?.squad?.position || "",
        },
      });

      this.data.push({
        name: "Shirt Number " + row,
        value: {
          text:
            player?.squad?.shirt_number ??
            player?.squad?.opta_shirt_number ??
            "",
        },
      });

      this.data.push({
        name: "Image " + row,
        value: {
          image: "${user}" + player?.squad?.player?.image,
        },
      });

      this.data.push({
        name: "X/Y " + row,
        value: {
          position: {
            x: average?.x ?? 0,
            y: average?.y ?? 0,
          },
        },
      });
    });

    this.sendPreview(this.scene, 500, this.data, "IN");
  }

  animate({ options }) {
    this.onAir = true;
    this.sendAnimate(this.scene, this.data);
  }
  animateOff({ options }) {
    this.onAir = false;
    this.sendAnimateOff(this.scene, []);
  }
}
